import { Button, Destructive, Icon, useToast } from '@efira/ui';

import { useDeleteBinding, useRestoreBinding } from '../../../api/queries';
import type { Binding, Collection } from '../../../api/queries';
import { APPLY_MOMENT_LABELS, label, plural, valueLabel } from '../../../bindings/labels';

/**
 * Состав участников: дорожки заезда, стороны «стенки», команды.
 *
 * ПЕРЕЕХАЛО СЮДА С ОТДЕЛЬНОГО ЭКРАНА (решение владельца 2026-09-19). Состав -
 * это настройка игры, и место ему в «Настройках игры» отдельной группой, а не
 * на экране, куда надо уходить из консоли.
 *
 * ТРИ МОДЕЛИ ИСТОЧНИКА - ТРИ РАЗНЫХ ОТВЕТА на вопрос «можно ли добавить ещё»:
 * закрытое перечисление - пока остались неиспользованные значения; открытый
 * справочник - всегда; фиксированные слоты - нельзя, они включаются. У
 * эталона разница нигде не объяснена, и кнопка «Add push strength» появляется
 * только после удаления группы.
 *
 * МОМЕНТ ПРИМЕНЕНИЯ НАПИСАН СЛОВАМИ. В `horse-race` добавленная дорожка
 * появляется только после перезапуска, в `territory-war` - сразу с
 * автоперезапуском, и снаружи это неразличимо: элемент просто не появляется.
 */
export function Collections({
  gameId,
  collections,
  bindings,
}: {
  gameId: string;
  collections: Collection[];
  bindings: Binding[];
}) {
  if (collections.length === 0) {
    return null;
  }

  return (
    <div data-gs-group="collections" className="border-foreground/10 mt-4 border-t pt-3">
      <p
        data-gs-group-label="collections"
        className="text-foreground/80 mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase"
      >
        <Icon name="games" size="sm" />
        Состав участников
      </p>

      <div className="flex flex-col gap-3">
        {collections.map((collection) => (
          <CollectionCard
            key={collection.id}
            gameId={gameId}
            collection={collection}
            bindings={bindings}
          />
        ))}
      </div>
    </div>
  );
}

function CollectionCard({
  gameId,
  collection,
  bindings,
}: {
  gameId: string;
  collection: Collection;
  bindings: Binding[];
}) {
  const restore = useRestoreBinding(gameId);
  const remove = useDeleteBinding(gameId);
  const toast = useToast();

  /* Состав читается ИЗ ПРИВЯЗОК, а не только из схемы: у эталона в
     `horse-race` дорожки заданы значениями параметра в привязках, и вывести
     состав из схемы нельзя в принципе. */
  const used = new Set<string>();

  for (const binding of bindings) {
    for (const value of Object.values(binding.params ?? {})) {
      used.add(String(value));
    }
  }

  const source = collection.source;
  const closed = source.kind === 'enum';
  const values = closed ? source.values : [...used];
  const free = closed ? source.values.filter((value) => !used.has(value)) : [];

  const usedCount = values.filter((value) => used.has(value)).length;
  const atMax = usedCount >= collection.max;
  const exhausted = closed && free.length === 0;
  const below = usedCount < collection.min;

  return (
    <section data-gs-collection={collection.id} className="flex flex-col gap-2">
      <p className="text-sm font-medium">{label(collection.labelKey)}</p>
      <p className="text-foreground/80 text-sm">
        {APPLY_MOMENT_LABELS[collection.applyMoment] ?? collection.applyMoment}
        {' · '}
        {source.kind === 'enum'
          ? `закрытый набор: ${plural(source.values.length, ['значение', 'значения', 'значений'])}`
          : source.kind === 'directory'
            ? 'открытый справочник: значения ищутся поиском'
            : `${source.count} слотов, включаются переключателем`}
      </p>

      <ul className="divide-foreground/10 flex flex-col divide-y">
        {values.map((value) => {
          const owner = bindings.find((binding) =>
            Object.values(binding.params ?? {}).includes(value),
          );

          return (
            <li
              key={value}
              data-gs-collection-item={value}
              className="flex items-center gap-2 py-2 text-sm"
            >
              <span className="min-w-0 flex-1 truncate">{valueLabel(value)}</span>

              <Button
                variant="ghost"
                size="s"
                className="px-1"
                data-gs-collection-restore={value}
                aria-label={`Вернуть заводское для «${valueLabel(value)}»`}
                title={
                  owner === undefined
                    ? 'Элемент не привязан — возвращать нечего'
                    : 'Вернуть заводское'
                }
                disabled={owner === undefined}
                onClick={() => {
                  if (owner !== undefined) {
                    restore.mutate(owner.key, {
                      onSuccess: () => {
                        toast.show({ tone: 'success', title: 'Вернули заводское' });
                      },
                    });
                  }
                }}
              >
                <Icon name="arrowReset" size="sm" />
              </Button>

              <Destructive
                id="collection-delete"
                className="px-1"
                data={{ 'data-gs-collection-remove': value }}
                ariaLabel={`Удалить «${valueLabel(value)}»`}
                icon={<Icon name="delete" size="sm" />}
                disabled={owner === undefined}
                title={`Удалить «${valueLabel(value)}»?`}
                confirm="Удалить"
                onConfirm={() => {
                  if (owner !== undefined) remove.mutate(owner.key);
                }}
              >
                Значение уйдёт из коллекции вместе с его привязкой. Заводское вернёт кнопка возврата
                рядом.
              </Destructive>
            </li>
          );
        })}
      </ul>

      {/* Правила 2 и 3: кнопка видна ВСЕГДА, при исчерпанном наборе неактивна
          и объясняет причину, предел назван числом рядом. */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="s"
          className="gap-1"
          data-gs-collection-add={collection.id}
          disabled={exhausted || atMax}
          title={
            exhausted
              ? 'Все значения набора уже использованы'
              : atMax
                ? `Предел — ${collection.max} элементов`
                : undefined
          }
        >
          <Icon name="add" size="sm" />
          {source.kind === 'directory' ? 'Добавить из справочника' : 'Добавить'}
        </Button>

        <span
          className={
            below
              ? 'text-foreground inline-flex items-center gap-1 text-xs font-medium'
              : 'text-foreground/80 text-xs'
          }
        >
          {below && <Icon name="error" size="sm" tone="danger" />}
          {usedCount} из {collection.max}
          {collection.min > 0 ? `, минимум ${collection.min}` : ''}
        </span>
      </div>

      {below && (
        <p className="text-foreground/80 text-sm">
          Ниже минимума: добавьте элемент, иначе игра возьмёт заводской состав.
        </p>
      )}

      {exhausted && (
        <p className="text-foreground/80 text-sm">
          Все значения использованы. Чтобы добавить, сначала удалите лишний — кнопка остаётся на
          месте, чтобы о возможности было видно всегда.
        </p>
      )}
    </section>
  );
}
